import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import { getPublisherIdByPortalId } from 'utils/portalUtils';

import { ThresholdData } from 'components/Pricing/Pricing.types';

import { PublisherInfo } from '../common/contracts';
import { IPermissions } from '../common/permissions.types';
import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';
import { permissionsUtil } from '../utils/permissionsUtil';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock,
  getCustomHeader
} from './api.utils';
import { useUserDetails } from './useUserDetails';

interface UpdateSettingsParams {
  setting: string;
  isActive: boolean;
}

export default function usePublisher(
  publisherId?: string,
  deactivate?: boolean
) {
  const axios = useAxios();
  const { portalId } = useParams();
  const { userDetails } = useUserDetails();

  const effectivePublisherId = publisherId || (portalId ? getPublisherIdByPortalId(portalId, userDetails) : undefined);

  const canAccessUsers = publisherId
    ? !!permissionsUtil.canAccessUsers(
        localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) || {},
        publisherId
      )
    : !!portalId;

  const canAccessPublisher = publisherId
    ? !!permissionsUtil.canAccessPublisher(
        localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) || {},
        publisherId
      )
    : !!portalId;

  const hasPublisherHeader = !!effectivePublisherId;

  const getPublisher = useQuery<PublisherInfo, Error>({
    ...fetchConfig.general,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: canAccessPublisher && hasPublisherHeader,
    queryKey: [EQueryKeys.PUBLISHER, effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.PUBLISHER),
        {},
        { 'x-publishers': effectivePublisherId! }
      );
    },
    onSuccess: (data) => {
      if (data?.publisherSolutionModel) {
        localStorageUtil.set(
          ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL,
          data.publisherSolutionModel
        );
      }
    }
  });

  const getPublisherSettings = useQuery<any, Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.PUBLISHER_SETTINGS],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.PUBLISHER_SETTINGS),
        {},
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
    },
    enabled: false
  });

  const getUsersByPublisher = useQuery({
    ...fetchConfig.single,
    queryKey: [EApiRoutes.PUBLISHER_USERS],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.PUBLISHER_USERS),
        {},
        portalId ? { 'x-portal': portalId } : getCustomHeader(publisherId)
      );
    },
    enabled: canAccessUsers
  });

  const updatePublisher = useMutation(async (data: Partial<PublisherInfo>) => {
    return await axios.put(
      getApiRouteOrMock(EApiRoutes.PUBLISHER),
      data,
      effectivePublisherId ? { 'x-publishers': effectivePublisherId } : undefined
    );
  }, {});

  const updatePublisherSettings = useMutation(
    async (params: UpdateSettingsParams) => {
      const apiRoute = getApiRouteOrMock(EApiRoutes.PUBLISHER_SETTINGS);
      const { setting, isActive } = params;
      const url = `${apiRoute}/${setting}?isActive=${isActive}`;
      return await axios.put(url, {});
    },
    {}
  );

  const updateConversionThresholds = useMutation(
    async (priceCheckerConversionsThresholds: ThresholdData[]) => {
      return await axios.put(
        getApiRouteOrMock(EApiRoutes.EXCHANGE_RATES_THRESHOLDS),
        priceCheckerConversionsThresholds
      );
    }
  );

  const updateResetTimezone = useMutation(
    async (data: { popUpStartHourUTC: number; timeZoneOffset: number }) => {
      return await axios.put(
        getApiRouteOrMock(EApiRoutes.PUBLISHER_SETTINGS),
        data,
        publisherId ? { 'x-publishers': publisherId } : undefined
      );
    }
  );

  return {
    getPublisher,
    getPublisherSettings,
    updatePublisher,
    getUsersByPublisher,
    updatePublisherSettings,
    updateConversionThresholds,
    updateResetTimezone
  };
}
